import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scale as ScaleAuditIcon } from 'lucide-react';
import { Plus, Search, ChevronDown, ChevronUp, Eye, Edit, Trash2, BarChart3, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUpDown, ArrowUp, ArrowDown, RotateCcw } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { expedientesAPI, contactosAPI, configuracionAPI, procedimientosAPI } from '../api';
import { toast } from '../hooks/use-toast';
import ContactoSearch from '../components/ContactoSearch';
import VoiceSearchButton from '../components/VoiceSearchButton';

const ExpedientesNew = ({ autoOpenCreate = false, preselectedContactoId = null }) => {
  const navigate = useNavigate();
  const [expedientes, setExpedientes] = useState([]);
  const [procResumen, setProcResumen] = useState({});
  const [contactos, setContactos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRama, setFilterRama] = useState('all');
  const [filterEstado, setFilterEstado] = useState('all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [selectedJuzgado, setSelectedJuzgado] = useState(null);
  const [selectedProcurador, setSelectedProcurador] = useState(null);
  const [selectedContrario, setSelectedContrario] = useState(null);
  const [showCreateContacto, setShowCreateContacto] = useState(false);
  const [createContactoType, setCreateContactoType] = useState(null);
  const [createContactoNombre, setCreateContactoNombre] = useState("");
  
  // Estados controlados para campos Select (fix: Radix Select no funciona con FormData)
  const [formRama, setFormRama] = useState("Civil");
  const [formFase, setFormFase] = useState("Inicio");
  const [formTipo, setFormTipo] = useState('General');
  const [tiposProcedimiento, setTiposProcedimiento] = useState(['Ordinario', 'Verbal', 'Monitorio', 'Ejecutivo', 'General']);
  const [formEstado, setFormEstado] = useState("activo");
  
  // Ordenación
  const [sortField, setSortField] = useState('numero_expediente');
  const [sortDirection, setSortDirection] = useState('desc');

  // Columnas redimensionables
  const DEFAULT_COLUMN_WIDTHS = {
    expand: 40,
    numero: 100,
    titulo: 200,
    descripcion: 200,
    cliente: 150,
    fecha: 90,
    expJudicial: 120,
    acciones: 80
  };
  
  const [columnWidths, setColumnWidths] = useState(() => {
    const saved = localStorage.getItem('expedientes_column_widths');
    return saved ? JSON.parse(saved) : DEFAULT_COLUMN_WIDTHS;
  });
  
  const resizingRef = useRef(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  // Guardar anchos de columna en localStorage
  useEffect(() => {
    localStorage.setItem('expedientes_column_widths', JSON.stringify(columnWidths));
  }, [columnWidths]);

  // Cargar tipos de procedimiento configurables
  useEffect(() => {
    configuracionAPI.getItems('tipos-procedimiento')
      .then(res => {
        const items = Array.isArray(res.data) ? res.data : (res.data?.items || []);
        if (items.length > 0) setTiposProcedimiento(items.map(i => i.nombre));
      })
      .catch(() => {});
  }, []);

  // Auto-abrir dialog si viene de contacto
  useEffect(() => {
    if (autoOpenCreate) {
      setIsCreateDialogOpen(true);
    }
    if (preselectedContactoId) {
      // Cargar el contacto preseleccionado
      const loadPreselectedContacto = async () => {
        try {
          const response = await contactosAPI.getById(preselectedContactoId);
          if (response.data) {
            setSelectedCliente(response.data);
          }
        } catch (error) {
          console.error('Error cargando contacto preseleccionado:', error);
        }
      };
      loadPreselectedContacto();
    }
  }, [autoOpenCreate, preselectedContactoId]);

  // Handlers para redimensionar columnas
  const handleMouseDown = useCallback((e, columnKey) => {
    e.preventDefault();
    resizingRef.current = columnKey;
    startXRef.current = e.clientX;
    startWidthRef.current = columnWidths[columnKey];
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [columnWidths]);

  const handleMouseMove = useCallback((e) => {
    if (!resizingRef.current) return;
    const diff = e.clientX - startXRef.current;
    const newWidth = Math.max(50, startWidthRef.current + diff);
    setColumnWidths(prev => ({
      ...prev,
      [resizingRef.current]: newWidth
    }));
  }, []);

  const handleMouseUp = useCallback(() => {
    resizingRef.current = null;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [handleMouseMove]);

  const resetColumnWidths = () => {
    setColumnWidths(DEFAULT_COLUMN_WIDTHS);
    toast({
      title: "Columnas restablecidas",
      description: "Los anchos de columna se han restablecido"
    });
  };

  // Paginación
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false
  });

  // Debounce para búsqueda
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const ramas = ['Civil', 'Mercantil', 'Laboral', 'Penal', 'Administrativo', 'Familia'];
  const fases = ['Inicio', 'Demanda', 'Contestación', 'Pruebas', 'Sentencia', 'Recurso'];

  // Debounce del término de búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPagination(prev => ({ ...prev, page: 1 })); // Reset a página 1 al buscar
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Cargar contactos (para el selector de clientes)
  useEffect(() => {
    const loadContactos = async () => {
      try {
        const res = await contactosAPI.getAll({ tipo: 'cliente', limit: 1000 });
        if (res.data && res.data.data) {
          setContactos(res.data.data);
        } else {
          setContactos(Array.isArray(res.data) ? res.data : []);
        }
      } catch (error) {
        console.error('Error cargando contactos:', error);
      }
    };
    loadContactos();
  }, []);

  // Cargar expedientes cuando cambia la página, búsqueda o filtros
  const loadExpedientes = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };
      
      if (debouncedSearch && debouncedSearch.length >= 2) {
        params.search = debouncedSearch;
      }
      
      if (filterRama && filterRama !== 'all') {
        params.rama = filterRama;
      }
      
      if (filterEstado && filterEstado !== 'all') {
        params.estado = filterEstado;
      }
      
      const res = await expedientesAPI.getAll(params);
      
      // El backend devuelve { data: [...], pagination: {...} }
      if (res.data && res.data.data) {
        setExpedientes(res.data.data);
        setPagination(prev => ({
          ...prev,
          ...res.data.pagination
        }));
      } else {
        // Fallback si el backend devuelve array directo (compatibilidad)
        setExpedientes(Array.isArray(res.data) ? res.data : []);
      }
      // v129: resumen de procedimientos judiciales para la columna
      const _list = (res.data && res.data.data) ? res.data.data : (Array.isArray(res.data) ? res.data : []);
      const _ids = _list.map((e) => e.id).filter(Boolean);
      if (_ids.length) {
        procedimientosAPI.resumen(_ids).then((r) => setProcResumen(r.data || {})).catch(() => {});
      }
    } catch (error) {
      console.error('Error cargando expedientes:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los expedientes",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, debouncedSearch, filterRama, filterEstado]);

  useEffect(() => {
    loadExpedientes();
  }, [loadExpedientes]);

  // Cambiar filtros resetea la página
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [filterRama, filterEstado]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      setPagination(prev => ({ ...prev, page: newPage }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const toggleRow = (id) => {
    setExpandedRows(prev => 
      prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
    );
  };

  // Función para manejar ordenación
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Icono de ordenación
  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-3 h-3 ml-1 opacity-50" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1" />
      : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  // Expedientes ordenados
  const sortedExpedientes = useMemo(() => {
    if (!expedientes.length) return [];
    
    return [...expedientes].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortField) {
        case 'numero_expediente':
          aVal = (a.numero_expediente || '').toLowerCase();
          bVal = (b.numero_expediente || '').toLowerCase();
          break;
        case 'fecha_inicio':
          aVal = a.fecha_inicio ? new Date(a.fecha_inicio).getTime() : 0;
          bVal = b.fecha_inicio ? new Date(b.fecha_inicio).getTime() : 0;
          break;
        case 'cliente_nombre':
          aVal = (a.cliente_nombre || '').toLowerCase();
          bVal = (b.cliente_nombre || '').toLowerCase();
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [expedientes, sortField, sortDirection]);

  const handleCreateExpediente = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      const data = {
        titulo: formData.get('titulo'),
        cliente_id: selectedCliente?.id || null,
        rama: formRama,
        tipo_procedimiento: formTipo || 'General',
        fase: formFase,
        estado: formEstado,
        descripcion: formData.get('descripcion'),
        valor_economico: Number(formData.get('valorEconomico')) || 0,
        fecha_inicio: formData.get('fecha_inicio') || null,
        expediente_judicial: formData.get('expediente_judicial') || null,
        nig: formData.get('nig') || null,
        juzgado: selectedJuzgado?.nombre || null,
        juzgado_id: selectedJuzgado?.id || null,
        procurador: selectedProcurador?.nombre || null,
        contrario: selectedContrario?.nombre || null,
        contrario_id: selectedContrario?.id || null
      };

      const response = await expedientesAPI.create(data);
      toast({
        title: "Expediente creado",
        description: "El expediente se ha creado correctamente"
      });
      setIsCreateDialogOpen(false);
      navigate(`/expedientes/${response.data.id}`);
      setSelectedCliente(null);
      setSelectedJuzgado(null);
      setSelectedProcurador(null);
      setSelectedContrario(null);
      loadExpedientes();
    } catch (error) {
      console.error('Error creando expediente:', error);
      toast({
        title: "Error",
        description: "No se pudo crear el expediente",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Está seguro de eliminar este expediente?')) {
      try {
        await expedientesAPI.delete(id);
        toast({
          title: "Expediente eliminado",
          description: "El expediente se ha eliminado correctamente"
        });
        loadExpedientes();
      } catch (error) {
        console.error('Error eliminando expediente:', error);
        toast({
          title: "Error",
          description: "No se pudo eliminar el expediente",
          variant: "destructive"
        });
      }
    }
  };

  const getEstadoBadge = (estado) => {
    switch (estado) {
      case 'activo':
        return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Activo</Badge>;
      case 'suspendido':
        return <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100">Suspendido</Badge>;
      case 'cerrado':
        return <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100">Cerrado</Badge>;
      default:
        return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">{estado}</Badge>;
    }
  };

  // Componente de paginación
  const PaginationControls = () => {
    const { page, total_pages, total, has_next, has_prev, limit } = pagination;
    const startRecord = total > 0 ? (page - 1) * limit + 1 : 0;
    const endRecord = Math.min(page * limit, total);

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 bg-gray-50 border-t">
        <div className="text-sm text-gray-600">
          Mostrando <span className="font-medium">{startRecord}</span> a <span className="font-medium">{endRecord}</span> de <span className="font-medium">{total}</span> expedientes
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => handlePageChange(1)}
            disabled={!has_prev}
            title="Primera página"
          >
            <ChevronsLeft className="w-4 h-4" />
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => handlePageChange(page - 1)}
            disabled={!has_prev}
            title="Página anterior"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-1 px-2">
            <span className="text-sm font-medium">Página</span>
            <Input 
              type="number" 
              min={1} 
              max={total_pages}
              value={page}
              onChange={(e) => {
                const newPage = parseInt(e.target.value);
                if (!isNaN(newPage) && newPage >= 1 && newPage <= total_pages) {
                  handlePageChange(newPage);
                }
              }}
              className="w-16 h-8 text-center text-sm"
            />
            <span className="text-sm text-gray-500">de {total_pages}</span>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => handlePageChange(page + 1)}
            disabled={!has_next}
            title="Página siguiente"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => handlePageChange(total_pages)}
            disabled={!has_next}
            title="Última página"
          >
            <ChevronsRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Expedientes</h1>
          <p className="text-gray-600 mt-1">Gestión de expedientes del despacho</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/expedientes/estadisticas')}>
            <BarChart3 className="w-4 h-4 mr-2" />
            Estadísticas
          </Button>
          <Button variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => navigate('/expedientes/pago-tasas')} data-testid="btn-pago-tasas-expedientes">
            Pago de Tasas
          </Button>
          <Button variant="outline" className="border-purple-300 text-purple-700 hover:bg-purple-50" onClick={() => navigate('/expedientes/analisis-hipotecas')} data-testid="btn-analisis-hipotecas">
            <BarChart3 className="w-4 h-4 mr-2" />
            Análisis IA Hipotecas
          </Button>
          <Button variant="outline" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" onClick={() => navigate('/expedientes/analisis-ca-hipotecaria')} data-testid="btn-analisis-ca-hipotecaria">
            <BarChart3 className="w-4 h-4 mr-2" />
            Análisis IA CA Hipotecaria
          </Button>
          <Button variant="outline" className="border-slate-400 text-slate-700 hover:bg-slate-100" onClick={() => navigate('/expedientes/auditorias')} data-testid="btn-auditorias-procesales">
            <ScaleAuditIcon className="w-4 h-4 mr-2" />
            Auditorías
          </Button>
          <Button onClick={() => setIsCreateDialogOpen(true)} data-testid="new-expediente-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Expediente
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1 flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Buscar en número, título, juzgado, procurador, rama, fase..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-expedientes-input"
                />
              </div>
              <VoiceSearchButton
                onResult={(text) => setSearchTerm(text)}
                data-testid="voice-search-expedientes"
              />
            </div>
            <Select value={filterRama} onValueChange={setFilterRama}>
              <SelectTrigger className="w-full md:w-48" data-testid="filter-rama-select">
                <SelectValue placeholder="Rama" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las ramas</SelectItem>
                {ramas.map(rama => (
                  <SelectItem key={rama} value={rama}>{rama}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterEstado} onValueChange={setFilterEstado}>
              <SelectTrigger className="w-full md:w-40" data-testid="filter-estado-select">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="activo">Activo</SelectItem>
                <SelectItem value="suspendido">Suspendido</SelectItem>
                <SelectItem value="cerrado">Cerrado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {debouncedSearch && (
            <div className="mt-2 text-sm text-gray-500">
              Buscando: "<span className="font-medium">{debouncedSearch}</span>" - {pagination.total} resultados encontrados
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card>
        <CardContent className="p-0">
          {/* Botón para restablecer columnas */}
          <div className="flex justify-end p-2 border-b bg-gray-50">
            <Button variant="ghost" size="sm" onClick={resetColumnWidths} title="Restablecer anchos de columna">
              <RotateCcw className="w-4 h-4 mr-1" />
              Restablecer columnas
            </Button>
          </div>
          {loading ? (
            <div className="p-8 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th style={{ width: columnWidths.expand }} className="px-2 py-2 relative">
                        <div 
                          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400"
                          onMouseDown={(e) => handleMouseDown(e, 'expand')}
                        />
                      </th>
                      <th 
                        style={{ width: columnWidths.numero }}
                        className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none relative"
                        onClick={() => handleSort('numero_expediente')}
                      >
                        <div className="flex items-center">
                          Nº Exp.
                          <SortIcon field="numero_expediente" />
                        </div>
                        <div 
                          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400"
                          onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, 'numero'); }}
                        />
                      </th>
                      <th style={{ width: columnWidths.titulo }} className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase relative">
                        Título
                        <div 
                          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400"
                          onMouseDown={(e) => handleMouseDown(e, 'titulo')}
                        />
                      </th>
                      <th style={{ width: columnWidths.descripcion }} className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase relative">
                        Descripción
                        <div 
                          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400"
                          onMouseDown={(e) => handleMouseDown(e, 'descripcion')}
                        />
                      </th>
                      <th 
                        style={{ width: columnWidths.cliente }}
                        className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none relative"
                        onClick={() => handleSort('cliente_nombre')}
                      >
                        <div className="flex items-center">
                          Participantes
                          <SortIcon field="cliente_nombre" />
                        </div>
                        <div 
                          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400"
                          onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, 'cliente'); }}
                        />
                      </th>
                      <th 
                        style={{ width: columnWidths.fecha }}
                        className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none relative"
                        onClick={() => handleSort('fecha_inicio')}
                      >
                        <div className="flex items-center">
                          F. Inicio
                          <SortIcon field="fecha_inicio" />
                        </div>
                        <div 
                          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400"
                          onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, 'fecha'); }}
                        />
                      </th>
                      <th style={{ width: columnWidths.expJudicial }} className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase relative">
                        Exp. Judicial
                        <div 
                          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400"
                          onMouseDown={(e) => handleMouseDown(e, 'expJudicial')}
                        />
                      </th>
                      <th style={{ width: columnWidths.acciones }} className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {sortedExpedientes.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-2 py-8 text-center text-gray-500">
                          {debouncedSearch ? 'No se encontraron expedientes con esa búsqueda' : 'No hay expedientes'}
                        </td>
                      </tr>
                    ) : (
                      sortedExpedientes.map((exp) => (
                        <React.Fragment key={exp.id}>
                          <tr className="hover:bg-gray-50" data-testid={`expediente-row-${exp.id}`}>
                            <td className="px-2 py-2">
                              <button
                                onClick={() => toggleRow(exp.id)}
                                className="p-1 hover:bg-gray-200 rounded"
                              >
                                {expandedRows.includes(exp.id) ? 
                                  <ChevronUp className="w-4 h-4" /> : 
                                  <ChevronDown className="w-4 h-4" />
                                }
                              </button>
                            </td>
                            <td className="px-2 py-2 font-medium text-blue-600">
                              <a href={`/expedientes/${exp.id}`} onClick={(e) => { e.preventDefault(); navigate(`/expedientes/${exp.id}`); }} className="text-xs hover:underline">
                                {exp.numero_expediente}
                              </a>
                            </td>
                            <td className="px-2 py-2 text-gray-900 text-xs">
                              <div className="line-clamp-2" title={exp.titulo}>{exp.titulo || '-'}</div>
                            </td>
                            <td className="px-2 py-2 text-gray-600 text-xs">
                              <div className="line-clamp-2" title={exp.descripcion}>{exp.descripcion || '-'}</div>
                            </td>
                            <td className="px-2 py-2 text-xs">
                              <div className="space-y-0.5">
                                {exp.cliente_id ? (
                                  <div className="flex items-center gap-1">
                                    <span className="text-gray-400 text-[10px]">C:</span>
                                    <a 
                                      href={`/contactos/${exp.cliente_id}`}
                                      onClick={(e) => { e.preventDefault(); navigate(`/contactos/${exp.cliente_id}`); }}
                                      className="text-blue-600 hover:text-blue-800 hover:underline line-clamp-1 text-left"
                                      title={exp.cliente_nombre}
                                    >
                                      {exp.cliente_nombre || '-'}
                                    </a>
                                  </div>
                                ) : exp.cliente_nombre ? (
                                  <div className="flex items-center gap-1">
                                    <span className="text-gray-400 text-[10px]">C:</span>
                                    <span className="text-gray-600 line-clamp-1">{exp.cliente_nombre}</span>
                                  </div>
                                ) : null}
                                {exp.contrario && (
                                  <div className="flex items-center gap-1">
                                    <span className="text-gray-400 text-[10px]">PC:</span>
                                    <span className="text-gray-600 line-clamp-1" title={exp.contrario}>{exp.contrario}</span>
                                  </div>
                                )}
                                {!exp.cliente_nombre && !exp.contrario && (
                                  <span className="text-gray-400">-</span>
                                )}
                              </div>
                            </td>
                            <td className="px-2 py-2 text-gray-600 text-xs whitespace-nowrap">
                              {exp.fecha_inicio ? new Date(exp.fecha_inicio).toLocaleDateString('es-ES') : '-'}
                            </td>
                            <td className="px-2 py-2 text-gray-600 text-xs">
                              {(() => {
                                const arr = procResumen[String(exp.id)];
                                if (arr && arr.length) {
                                  const f = arr[0];
                                  const label = `${f.tipo}${f.numero ? ' ' + f.numero : ''}`;
                                  const full = arr.map((a) => `${a.tipo}${a.numero ? ' ' + a.numero : ''}${a.organo ? ' — ' + a.organo : ''}`).join(' · ');
                                  return <div className="line-clamp-1" title={full}>{label}{arr.length > 1 ? ` (+${arr.length - 1})` : ''}</div>;
                                }
                                return <div className="line-clamp-1" title={exp.expediente_judicial}>{exp.expediente_judicial || '-'}</div>;
                              })()}
                            </td>
                            <td className="px-2 py-2">
                              <div className="flex items-center justify-end gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => navigate(`/expedientes/${exp.id}`)}
                                  title="Ver expediente"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => navigate(`/expedientes/${exp.id}?edit=true`)}
                                  title="Editar expediente"
                                  className="text-blue-600 hover:text-blue-700"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleDelete(exp.id)}
                                  className="text-red-600 hover:text-red-700"
                                  title="Eliminar expediente"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                          {expandedRows.includes(exp.id) && (
                            <tr className="bg-gray-50">
                              <td colSpan="8" className="px-8 py-4">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                  <div>
                                    <span className="text-gray-500">Juzgado:</span>
                                    <p className="font-medium">{exp.juzgado || '-'}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Procurador:</span>
                                    <p className="font-medium">{exp.procurador || '-'}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Contrario:</span>
                                    <p className="font-medium">{exp.contrario || '-'}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Valor económico:</span>
                                    <p className="font-medium">{(exp.valor_economico || 0).toLocaleString('es-ES')}€</p>
                                  </div>
                                  <div className="col-span-2 md:col-span-4">
                                    <span className="text-gray-500">Descripción:</span>
                                    <p className="font-medium">{exp.descripcion || 'Sin descripción'}</p>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Paginación */}
              {pagination.total > 0 && <PaginationControls />}
            </>
          )}
        </CardContent>
      </Card>

      {/* Dialog para crear expediente */}
      <Dialog open={isCreateDialogOpen} onOpenChange={(open) => {
        setIsCreateDialogOpen(open);
        if (!open) {
          setSelectedCliente(null);
          setSelectedJuzgado(null);
          setSelectedProcurador(null);
          setSelectedContrario(null);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuevo Expediente</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateExpediente} className="space-y-6">
            {/* Sección: Datos del Expediente */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-gray-700 border-b pb-2">Datos del Expediente</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="titulo">Título del expediente *</Label>
                  <Input id="titulo" name="titulo" required placeholder="Descripción breve del expediente" />
                </div>
                <div>
                  <Label htmlFor="rama">Rama *</Label>
                  <Select value={formRama} onValueChange={setFormRama}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ramas.map(rama => (
                        <SelectItem key={rama} value={rama}>{rama}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="estado">Estado</Label>
                  <Select value={formEstado} onValueChange={setFormEstado}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="activo">Activo</SelectItem>
                      <SelectItem value="suspendido">Suspendido</SelectItem>
                      <SelectItem value="cerrado">Cerrado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="valorEconomico">Valor económico (€)</Label>
                  <Input id="valorEconomico" name="valorEconomico" type="number" step="0.01" placeholder="0.00" />
                </div>
                <div>
                  <Label htmlFor="fecha_inicio">Fecha inicio</Label>
                  <Input id="fecha_inicio" name="fecha_inicio" type="date" />
                </div>
              </div>
            </div>

            {/* Sección: Datos Jurídicos */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-gray-700 border-b pb-2">Datos Jurídicos</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="tipo">Tipo procedimiento</Label>
                  <Select value={formTipo} onValueChange={setFormTipo}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {tiposProcedimiento.map(tp => (
                        <SelectItem key={tp} value={tp}>{tp}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="fase">Fase</Label>
                  <Select value={formFase} onValueChange={setFormFase}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {fases.map(fase => (
                        <SelectItem key={fase} value={fase}>{fase}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="expediente_judicial">Nº Expediente Judicial (histórico)</Label>
                  <Input id="expediente_judicial" name="expediente_judicial" placeholder="Ej: 123/2025 — los procedimientos se gestionan en la ficha" />
                </div>
                <div>
                  <Label htmlFor="nig">NIG</Label>
                  <Input id="nig" name="nig" placeholder="Número Identificación General" />
                </div>
              </div>
            </div>

            {/* Sección: Participantes */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-gray-700 border-b pb-2">Participantes</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Cliente</Label>
                  <ContactoSearch
                    value={selectedCliente}
                    onChange={setSelectedCliente}
                    onCreateNew={(nombre) => {
                      setCreateContactoType('cliente');
                      setCreateContactoNombre(nombre);
                      setShowCreateContacto(true);
                    }}
                    placeholder="Buscar cliente..."
                    tipoFilter="cliente"
                  />
                </div>
                <div>
                  <Label>Juzgado / Organismo</Label>
                  <ContactoSearch
                    value={selectedJuzgado}
                    onChange={setSelectedJuzgado}
                    onCreateNew={(nombre) => {
                      setCreateContactoType('juzgado');
                      setCreateContactoNombre(nombre);
                      setShowCreateContacto(true);
                    }}
                    placeholder="Buscar juzgado..."
                    tipoFilter="juzgado"
                  />
                </div>
                <div>
                  <Label>Procurador</Label>
                  <ContactoSearch
                    value={selectedProcurador}
                    onChange={setSelectedProcurador}
                    onCreateNew={(nombre) => {
                      setCreateContactoType('colaborador');
                      setCreateContactoNombre(nombre);
                      setShowCreateContacto(true);
                    }}
                    placeholder="Buscar procurador..."
                  />
                </div>
                <div>
                  <Label>Parte contraria</Label>
                  <ContactoSearch
                    value={selectedContrario}
                    onChange={setSelectedContrario}
                    onCreateNew={(nombre) => {
                      setCreateContactoType('cliente');
                      setCreateContactoNombre(nombre);
                      setShowCreateContacto(true);
                    }}
                    placeholder="Buscar parte contraria..."
                  />
                </div>
              </div>
            </div>

            {/* Sección: Descripción */}
            <div>
              <Label htmlFor="descripcion">Descripción / Observaciones</Label>
              <Textarea id="descripcion" name="descripcion" rows={3} placeholder="Información adicional del expediente..." />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">Crear Expediente</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog para crear nuevo contacto */}
      <Dialog open={showCreateContacto} onOpenChange={setShowCreateContacto}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Crear Nuevo Contacto</DialogTitle>
          </DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            try {
              const response = await contactosAPI.create({
                nombre: formData.get('contacto_nombre'),
                email: formData.get('contacto_email') || '',
                telefono: formData.get('contacto_telefono') || '',
                tipo: createContactoType || 'cliente',
                tipo_entidad: formData.get('tipo_entidad') || 'persona',
                nif_cif: ''
              });
              const nuevoContacto = response.data;
              
              // Asignar al campo correspondiente
              if (createContactoType === 'cliente') {
                if (!selectedCliente) setSelectedCliente(nuevoContacto);
                else setSelectedContrario(nuevoContacto);
              } else if (createContactoType === 'juzgado') {
                setSelectedJuzgado(nuevoContacto);
              } else {
                setSelectedProcurador(nuevoContacto);
              }
              
              setShowCreateContacto(false);
              setCreateContactoNombre('');
              toast({ title: "Contacto creado", description: `${nuevoContacto.nombre} creado correctamente` });
            } catch (error) {
              toast({ title: "Error", description: "No se pudo crear el contacto", variant: "destructive" });
            }
          }} className="space-y-4">
            <div>
              <Label htmlFor="tipo_entidad">Tipo de entidad</Label>
              <Select name="tipo_entidad" defaultValue="persona">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="persona">Persona física</SelectItem>
                  <SelectItem value="empresa">Empresa / Persona jurídica</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="contacto_nombre">Nombre *</Label>
              <Input id="contacto_nombre" name="contacto_nombre" defaultValue={createContactoNombre} required />
            </div>
            <div>
              <Label htmlFor="contacto_email">Email</Label>
              <Input id="contacto_email" name="contacto_email" type="email" />
            </div>
            <div>
              <Label htmlFor="contacto_telefono">Teléfono</Label>
              <Input id="contacto_telefono" name="contacto_telefono" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateContacto(false)}>Cancelar</Button>
              <Button type="submit">Crear y Seleccionar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpedientesNew;
